import { ParticleBackground } from "@dolfin/ui";
import { Sun, Moon } from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "../auth/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { useTheme } from "@/components/ThemeProvider";
import logoLight from "../logo-light.svg";
import logoDark from "../logo-dark.svg";
import { DevLoginPanel, EmailCodeSignIn, useDevLoginPersonas } from "@dolfin/ui";

export function LoginPage() {
  const { isAuthenticated, login, devLogin } = useAuth();
  // The portal is for client members, so only client-scoped personas: an organisation-only persona has no Client
  // membership and would be turned away after signing in.
  const personas = useDevLoginPersonas("/api/proxy/v1/auth/dev-login/personas")?.filter((p) => p.scope === "Client");
  const { theme, toggleTheme } = useTheme();
  const logo = theme === "dark" ? logoDark : logoLight;
  const [error, setError] = useState<string | null>(null);

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setError(null);
      const result = await login(tokenResponse.access_token);
      if (result.error) {
        setError(result.error);
      }
    },
    onError: () => setError("Google sign-in failed. Please try again."),
  });

  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <>
      <ParticleBackground theme={theme} prominent />
      <header className="flex h-14 items-center justify-between border-b bg-background/80 px-4 backdrop-blur-md">
        <a href="https://dolfinai.co" className="flex items-center gap-2 font-semibold">
          <img src={logo} alt="" className="size-7" />Dolfin
        </a>
        <Button variant="outline" size="icon" onClick={toggleTheme} aria-label="Toggle theme">
          {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
        </Button>
      </header>
      <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center p-4">
        <Card className="w-full max-w-sm bg-card/80 shadow-xl shadow-foreground/5 backdrop-blur-xl">
          <CardHeader className="items-center gap-4 text-center">
            <img src={logo} alt="Dolfin" className="mx-auto h-16 w-16" />
            <CardTitle className="font-display text-3xl">Dolfin Portal</CardTitle>
            <CardDescription>Sign in to manage your Clients</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <div className="flex w-full flex-col gap-2">
              <Button
                variant="outline"
                size="lg"
                className="w-full"
                onClick={() => googleLogin()}
              >
                <GoogleIcon />
                Continue with Google
              </Button>
              <EmailCodeSignIn onProof={async (proof) => (await login(proof, "email")).error ?? null} />
            </div>
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
            {personas && (
              <DevLoginPanel
                personas={personas}
                onSignIn={async (persona) => (await devLogin({ key: persona.key, label: persona.label })).error ?? null}
                resetUrl="/api/proxy/v1/auth/dev-login/reset"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
